/** @jsx jsx */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { token } from '@atlaskit/tokens';
import PremiumFeatureGate from '@atlassian/jira-atlassian-navigation/src/ui/plans/menu/premium-feature-gate/index.tsx';
import { L1_MENU_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { MENU_ID_PLANS } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';

import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { planUpsellEntryPoint } from '@atlassian/jira-portfolio-3-plan-upsell/entrypoint.tsx';
import { planUpsellRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/planUpsellRoute.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

const EMPTY_OBJECT = {};

const elemAfterStyles = cssMap({
	root: {
		paddingInline: token('space.075'),
		// Flip the chevron icon when the direction is right-to-left
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'[dir="rtl"] &': {
			transform: 'scaleX(-1)',
		},
	},
});

export const Nav4PlansPremiumUpsellFeatureGate = (): JSX.Element => {
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const { entryPointActions } = useEntryPoint(planUpsellEntryPoint, EMPTY_OBJECT);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions);

	if (fg('supercharge_timelines_with_plans_kill_switch')) {
		const [stwpConfig, fireStwpExposure] = UNSAFE_noExposureExp(
			'supercharge_timelines_with_plans_experiment',
		);
		const stwpCohort = stwpConfig.get('cohort', 'not-enrolled');

		if (stwpCohort === 'variation') {
			const { path } = planUpsellRoute;

			return (
				<Nav4MenuLinkItem
					menuId={MENU_ID_PLANS}
					elemBefore={icon}
					elemAfter={
						<div css={elemAfterStyles.root}>
							<ChevronRightIcon label="" color={token('color.icon')} size="small" />
						</div>
					}
					href={path}
					ref={triggerRef}
					onClick={fireStwpExposure}
				>
					{formattedMessage}
				</Nav4MenuLinkItem>
			);
		}

		return (
			<FlyoutMenuItem>
				<FlyoutMenuItemTrigger
					interactionName="nav4-sidebar-plans-premium-upsell"
					elemBefore={icon}
					onClick={fireStwpExposure}
				>
					{formattedMessage}
				</FlyoutMenuItemTrigger>
				<FlyoutMenuItemContent>
					<PremiumFeatureGate />
				</FlyoutMenuItemContent>
			</FlyoutMenuItem>
		);
	}

	return (
		<FlyoutMenuItem>
			<FlyoutMenuItemTrigger interactionName="nav4-sidebar-plans-premium-upsell" elemBefore={icon}>
				{formattedMessage}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent>
				<PremiumFeatureGate />
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
